import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

const INBOX_ITEMS = (userId) => collection(db, "notifications", userId, "items");

/** Ensure parent inbox doc exists */
async function ensureInboxDoc(userId) {
  await setDoc(
    doc(db, "notifications", userId),
    { userId, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function createUserNotification(userId, payload) {
  if (!userId) return null;
  await ensureInboxDoc(userId);
  return addDoc(INBOX_ITEMS(userId), {
    ...payload,
    read: false,
    createdAt: serverTimestamp(),
  });
}

export function subscribeToInboxNotifications(userId, onData, onError) {
  if (!userId) {
    onData([]);
    return () => {};
  }

  const alive = { current: true };

  const inboxUnsub = onSnapshot(
    query(INBOX_ITEMS(userId)),
    async (snap) => {
      if (!alive.current) return;

      let items = snap.docs
        .map((d) => ({ id: d.id, ...d.data(), _source: "inbox" }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      if (items.length === 0) {
        try {
          const legacySnap = await getDocs(
            query(collection(db, "notifications"), where("userId", "==", userId))
          );
          if (!alive.current) return;
          items = legacySnap.docs
            .map((d) => ({ id: d.id, ...d.data(), _source: "legacy" }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        } catch (err) {
          console.error("Legacy notifications load error:", err);
        }
      }

      if (alive.current) onData(items);
    },
    (err) => {
      if (!alive.current) return;
      console.error("Inbox notifications subscribe error:", err);
      onError?.(err);
    }
  );

  return () => {
    alive.current = false;
    inboxUnsub();
  };
}

export async function markInboxNotificationRead(userId, notification) {
  if (!userId || !notification?.id) return;

  if (notification._source === "legacy") {
    await updateDoc(doc(db, "notifications", notification.id), { read: true });
    return;
  }

  await updateDoc(doc(db, "notifications", userId, "items", notification.id), { read: true });
}

export async function markAllInboxNotificationsRead(userId, notifications) {
  if (!userId || !notifications?.length) return;

  const batch = writeBatch(db);
  notifications.filter((n) => !n.read).forEach((n) => {
    if (n._source === "legacy") {
      batch.update(doc(db, "notifications", n.id), { read: true });
    } else {
      batch.update(doc(db, "notifications", userId, "items", n.id), { read: true });
    }
  });
  await batch.commit();
}

export function getNotificationCategory(type, isCreator) {
  const t = String(type || "").toLowerCase();
  if (["drop", "broadcast", "view_alert", "view_milestone", "drop_milestone"].includes(t)) {
    return "drops";
  }
  if (["credit", "credits", "first_view", "referral", "pip"].includes(t)) {
    return "credits";
  }
  if (["payout", "earnings", "wallet", "drop_closed"].includes(t)) {
    return isCreator ? "wallet" : "all";
  }
  if (["wall", "appreciation", "note"].includes(t)) {
    return "wall";
  }
  return "all";
}

export function extractDropId(notification) {
  if (notification.dropId) return notification.dropId;
  const link = notification.link || "";
  const match = link.match(/\/(?:post|drop)\/([^/?#]+)/);
  return match?.[1] || null;
}

export function getDayLabel(timestamp) {
  if (!timestamp?.seconds) return "Earlier";
  const date = new Date(timestamp.seconds * 1000);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);

  if (date >= startOfToday) return "Today";
  if (date >= startOfYesterday) return "Yesterday";
  if (date >= startOfWeek) return "This week";
  return "Earlier";
}

export function timeSince(timestamp) {
  if (!timestamp?.seconds) return "just now";
  const diff = Date.now() - timestamp.seconds * 1000;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
  const days = Math.floor(diff / 86400000);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}
